import { createSelector } from 'reselect';
import { ApplicationState } from '../../reducers';
import { BrandCustomField, CustomFieldGroup } from '../../../types/custom_fields';
import { CustomFieldToBrand } from '../../reducers/parent/ParentCustomFieldsReducer';

// Base selectors
const getCustomFields = (state: ApplicationState) => state.parent.parentCustomFields.customFields;
const getGroups = (state: ApplicationState) => state.parent.parentCustomFields.groups;
const getFieldToBrands = (state: ApplicationState) => state.parent.parentCustomFields.fieldToBrands;
const getPendingChanges = (state: ApplicationState) =>
  state.parent.parentCustomFields.pendingChanges;

// Enhanced field type with brand associations
type EnhancedCustomField = BrandCustomField & {
  brandIds: number[];
  hasPendingChanges: boolean;
};

// Enhanced group type with processed fields
type ProcessedCustomFieldGroup = Omit<CustomFieldGroup, 'fields'> & {
  fields: EnhancedCustomField[];
};

// Create memoized field-to-brand map
const getFieldToBrandMap = createSelector(
  [getFieldToBrands],
  (fieldToBrands: CustomFieldToBrand[]): Record<number, number[]> => {
    return fieldToBrands.reduce((acc, item) => {
      acc[item.custom_field_id] = item.brand_ids;
      return acc;
    }, {} as Record<number, number[]>);
  }
);

// Main selector for processed custom field groups
export const getProcessedCustomFieldGroups = createSelector(
  [getCustomFields, getGroups, getFieldToBrandMap, getPendingChanges],
  (
    customFields: BrandCustomField[],
    groups: CustomFieldGroup[],
    fieldToBrandMap: Record<number, number[]>,
    pendingChanges: Record<number, number[]>
  ): ProcessedCustomFieldGroup[] => {
    if (!customFields.length) {
      return [];
    }

    // Process groups and add fields with brand info
    const processedGroups: ProcessedCustomFieldGroup[] = groups.map(group => {
      const groupFields = customFields
        .filter(field => field.group_id === group.id)
        .map(field => ({
          ...field,
          brandIds: pendingChanges[field.id] ?? fieldToBrandMap[field.id] ?? [],
          hasPendingChanges: field.id in pendingChanges,
        }));

      return {
        ...group,
        fields: groupFields,
      };
    });

    // Add ungrouped fields to "other" group
    const ungroupedFields = customFields
      .filter(field => field.group_id === null)
      .map(field => ({
        ...field,
        brandIds: pendingChanges[field.id] ?? fieldToBrandMap[field.id] ?? [],
        hasPendingChanges: field.id in pendingChanges,
      }));

    // Always add the "other" group, even if it has no fields
    processedGroups.push({
      id: 'other',
      name: 'other',
      fields: ungroupedFields,
      record_number: processedGroups.length + 1,
    });

    return processedGroups;
  }
);
